import { IconCheck, IconLock, IconX } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '../../../../../../i18n/hooks/useTranslation';
import AtsCvPreviewFrame from './AtsCvPreviewFrame';
import { INITIAL_ATS_CV_FORM } from '../engines/atsCvFormEngine.js';
import { buildAtsCvPreviewBlockMeta } from '../engines/atsCvPreviewBlocks.js';

// Built templates. `accent` is the mini-swatch gradient shown in the picker;
// the actual CV theme lives in ats-cv-preview under `.tpl-<id>`.
const TEMPLATES = [
    { id: 'classic', accent: 'from-orange-400 to-amber-500' },
    { id: 'modern', accent: 'from-sky-400 to-blue-500' },
    { id: 'professional', accent: 'from-emerald-400 to-teal-500' },
    { id: 'elegant', accent: 'from-violet-400 to-purple-500' },
    { id: 'sidebar', accent: 'from-blue-700 to-indigo-900' },
    { id: 'split', accent: 'from-zinc-500 to-zinc-800' },
    { id: 'banner', accent: 'from-teal-400 to-cyan-600' },
    { id: 'bold', accent: 'from-amber-400 to-yellow-600' },
];

// What the preview shows while the form is still empty, so a template can be judged
// on a CV with something in it rather than on a blank page.
const SAMPLE_FORM = {
    ...INITIAL_ATS_CV_FORM,
    fullName: 'Alex Morgan',
    jobTitle: 'Product Designer',
    email: 'alex.morgan@email.com',
    phone: '+1 555 010 2030',
    location: 'New York, USA',
    website: 'alexmorgan.design',
    summary: 'Product designer with six years of experience shipping web and mobile products. Comfortable owning a feature from research to release.',
    skills: 'Figma\nUser research\nPrototyping\nDesign systems',
    languages: 'English\nSpanish',
    experiences: [
        {
            id: 'sample-exp-1',
            jobTitle: 'Senior Product Designer',
            company: 'Northwind Labs',
            location: 'New York',
            startDate: 'Jan 2022',
            endDate: '',
            isCurrent: true,
            description: 'Led the redesign of the onboarding flow, lifting activation by 18%\nBuilt and maintained the company design system',
        },
        {
            id: 'sample-exp-2',
            jobTitle: 'Product Designer',
            company: 'Brightside Studio',
            location: 'Boston',
            startDate: 'Jun 2018',
            endDate: 'Dec 2021',
            isCurrent: false,
            description: 'Designed web apps for twelve client projects\nRan usability tests and turned findings into roadmap items',
        },
    ],
    education: [
        {
            id: 'sample-edu-1',
            degree: 'BA, Graphic Design',
            institution: 'State University',
            location: '',
            graduationYear: '2018',
            details: '',
        },
    ],
};

function Swatch({ accent, className = 'h-16' }) {
    return (
        <div className={`flex items-end gap-1 rounded-lg bg-gradient-to-br ${accent} p-2 ${className}`}>
            <div className="h-full w-1/3 rounded bg-white/85" />
            <div className="flex h-full w-2/3 flex-col justify-end gap-1">
                <div className="h-1.5 w-3/4 rounded bg-white/80" />
                <div className="h-1.5 w-full rounded bg-white/60" />
                <div className="h-1.5 w-2/3 rounded bg-white/60" />
            </div>
        </div>
    );
}

/**
 * Template chooser: one compact row on the page, and the full list in a dialog.
 *
 * The list grows with every new template, so it lives in a dialog instead of the
 * form column — templates on the left, a live preview of the CV in the picked
 * one on the right, and nothing changes until Apply.
 *
 * `lockedLabel` marks every template but `freeId` as app-only: it can be
 * previewed, but Apply says why it will not take instead of applying it.
 */
export default function AtsCvTemplatePicker({ selected = 'classic', onSelect, form, lockedLabel, freeId = 'classic' }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [previewId, setPreviewId] = useState(selected);

    const previewForm = form && buildAtsCvPreviewBlockMeta(form).blocks.length > 0 ? form : SAMPLE_FORM;
    const current = TEMPLATES.find((tpl) => tpl.id === selected) || TEMPLATES[0];

    const openDialog = () => {
        setPreviewId(selected);
        setOpen(true);
    };
    const closeDialog = () => setOpen(false);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open]);

    const isLocked = (id) => Boolean(lockedLabel) && id !== freeId;
    const previewLocked = isLocked(previewId);

    const apply = () => {
        if (previewLocked) return;
        onSelect?.(previewId);
        setOpen(false);
    };

    return (
        <>
            <div className="flex items-center gap-3 rounded-2xl border border-orange-200 bg-white px-4 py-3 dark:border-orange-900/40 dark:bg-zinc-900">
                <Swatch accent={current.accent} className="h-11 w-16 flex-none !p-1.5" />
                <div className="min-w-0 flex-1">
                    <p className="text-[11px] text-[#A8A099] dark:text-zinc-500">{t('atsCvMaker.templates.title')}</p>
                    <p className="truncate text-[13.5px] font-bold text-orange-700 dark:text-orange-300">
                        {t(`atsCvMaker.templates.items.${current.id}`)}
                    </p>
                </div>
                <button
                    type="button"
                    onClick={openDialog}
                    className="flex-none rounded-lg border border-orange-300 px-3 py-1.5 text-[12px] font-semibold text-orange-700 transition-colors hover:bg-orange-50 dark:border-orange-500/50 dark:text-orange-300 dark:hover:bg-orange-500/10"
                >
                    {t('atsCvMaker.templates.change')}
                </button>
            </div>

            {open && typeof document !== 'undefined' ? createPortal((
                // Portalled to the body: the form column sits inside blocks that grey
                // and clip their children, and a dialog rendered there goes with them.
                <div
                    className="fixed inset-0 z-[300] flex bg-[#1C1815]/80 p-3 backdrop-blur-[2px] sm:p-6"
                    role="presentation"
                    onClick={closeDialog}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-label={t('atsCvMaker.templates.modalTitle')}
                        onClick={(e) => e.stopPropagation()}
                        className="m-auto flex h-[90vh] w-full max-w-[1120px] flex-col overflow-hidden rounded-2xl border border-[#ECE8E4] bg-white shadow-[0_24px_60px_rgba(28,24,21,0.35)] dark:border-zinc-700 dark:bg-zinc-900"
                    >
                        <div className="flex h-12 flex-none items-center justify-between border-b border-[#ECE8E4] px-4 dark:border-zinc-800">
                            <h2 className="text-[14px] font-bold text-[#1C1815] dark:text-zinc-100">
                                {t('atsCvMaker.templates.modalTitle')}
                            </h2>
                            <button
                                type="button"
                                onClick={closeDialog}
                                aria-label={t('atsCvMaker.templates.cancel')}
                                className="flex h-8 w-8 items-center justify-center rounded-lg text-[#A8A099] transition-colors hover:bg-[#F7F5F3] hover:text-[#3D3833] dark:hover:bg-zinc-800"
                            >
                                <IconX size={16} stroke={2} />
                            </button>
                        </div>

                        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
                            {/* Left: the templates */}
                            <div className="flex flex-none gap-2.5 overflow-auto border-b border-[#ECE8E4] p-3 md:w-[250px] md:flex-col md:border-b-0 md:border-r dark:border-zinc-800">
                                {TEMPLATES.map((tpl) => {
                                    const name = t(`atsCvMaker.templates.items.${tpl.id}`);
                                    const isPreview = previewId === tpl.id;
                                    const isApplied = selected === tpl.id;
                                    return (
                                        <button
                                            key={tpl.id}
                                            type="button"
                                            onClick={() => setPreviewId(tpl.id)}
                                            aria-pressed={isPreview}
                                            title={name}
                                            className={`group relative w-[150px] flex-none overflow-hidden rounded-xl border p-2 text-left transition-colors md:w-full ${
                                                isPreview
                                                    ? 'border-orange-400 ring-2 ring-orange-400/30 dark:border-orange-500'
                                                    : 'border-zinc-200 hover:border-orange-300 dark:border-zinc-700 dark:hover:border-orange-500/60'
                                            }`}
                                        >
                                            <Swatch accent={tpl.accent} className="mb-2 h-14" />
                                            <div className="flex items-center justify-between gap-1">
                                                <span className="truncate text-[12px] font-semibold text-[#3D3833] dark:text-zinc-200">
                                                    {name}
                                                </span>
                                                {isLocked(tpl.id) ? (
                                                    <IconLock size={13} className="flex-none text-[#A8A099]" aria-hidden />
                                                ) : isApplied ? (
                                                    <span className="flex h-4 w-4 flex-none items-center justify-center rounded-full bg-orange-500 text-white">
                                                        <IconCheck size={11} stroke={3} />
                                                    </span>
                                                ) : null}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Right: the CV in the picked template */}
                            <div className="flex min-h-0 min-w-0 flex-1 flex-col p-3">
                                <AtsCvPreviewFrame key={previewId} form={previewForm} template={previewId} zoom={1} />
                            </div>
                        </div>

                        <div className="flex flex-none items-center justify-end gap-2 border-t border-[#ECE8E4] px-4 py-3 dark:border-zinc-800">
                            {previewLocked ? (
                                <span className="mr-auto text-[12px] text-[#A8A099] dark:text-zinc-500">{lockedLabel}</span>
                            ) : null}
                            <button
                                type="button"
                                onClick={closeDialog}
                                className="rounded-lg border border-zinc-200 px-4 py-2 text-[13px] font-semibold text-[#3D3833] transition-colors hover:bg-[#F7F5F3] dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
                            >
                                {t('atsCvMaker.templates.cancel')}
                            </button>
                            <button
                                type="button"
                                onClick={apply}
                                disabled={previewLocked}
                                className="rounded-lg bg-orange-500 px-5 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                {t('atsCvMaker.templates.apply')}
                            </button>
                        </div>
                    </div>
                </div>
            ), document.body) : null}
        </>
    );
}
